class CustomLineItemProperties {
  constructor() {
    console.log('CustomLineItemProperties constructor called');
    console.log('🔍 DEBUG: Current pathname:', window.location.pathname);
    console.log('🔍 DEBUG: Does it include "one-length-new"?', window.location.pathname.includes('one-length-new'));
    
    if (window.customLineItemPropertiesInitialized) {
      console.log('Already initialized, skipping...');
      return;
    }
    
    // Check if we have color swatch container (should exist on all product templates now)
    const hasColorSwatchContainer = document.getElementById('color-swatches-container');
    
    // Check if custom properties are needed 
    const hasCustomProperties = document.getElementById('properties_brand');
    
    // If no color swatches and no custom properties, skip entirely
    if (!hasColorSwatchContainer && !hasCustomProperties) {
      console.log('No color swatches or custom properties needed - skipping initialization');
      window.customLineItemPropertiesInitialized = true;
      return;
    }
    
    // Check for CPB conflicts that might interfere with our form
    const hasCPBPlaceholder = document.querySelector('.cpb-placeholder');
    const hasCPBButton = document.querySelector('.cpb-personalize-btn');
    
    // If CPB is active on this product, let it handle form interactions
    if (hasCPBPlaceholder && hasCPBButton) {
      console.log('CustomProductBuilder detected - skipping custom properties initialization');
      window.customLineItemPropertiesInitialized = true;
      return;
    }
    
    this.data = null;
    this.elements = {};
    this.colorData = null;
    this.basePrice = 1999; // Base price in cents ($19.99)
    this.clubPrice = 700;  // Price per club in cents ($7.00)
    this.init();
    window.customLineItemPropertiesInitialized = true;
  }

  async init() {
    try {
      await this.loadData();
      const colorData = await this.loadColorData();
      const tierProductsData = await this.loadTierProductsData();
      this.colorData = colorData; // Store color data for price calculations
      this.tierProducts = tierProductsData; // Store tier product data
      this.bindElements();
      
      // Initialize size selector with "Adult" as default
      this.initializeSizeSelector();
      
      // Set up comprehensive price update triggers
      this.setupPriceUpdateTriggers();
      
      // Always initialize color swatches if container exists
      if (this.elements.colorSwatchContainer) {
        console.log('Color swatch container found - initializing color swatches');
        this.initializeColorSwatches(colorData);
      }
      
      // Always initialize color dropdown if it exists (for one-length template)
      if (this.elements.color) {
        console.log('✅ Color dropdown found - initializing color options');
        this.initializeColorOptions(colorData);
        // Bind color dropdown events
        this.elements.color.addEventListener('change', (e) => this.handleColorDropdownChange(e));
      } else {
        console.log('❌ Color dropdown NOT found - checking all possible selectors...');
        console.log('- properties_color:', document.getElementById('properties_color'));
        console.log('- properties_color_ pattern:', document.querySelector('[id^="properties_color_"]'));
        console.log('- select[name="properties[Color]"]:', document.querySelector('select[name="properties[Color]"]'));
      }
      
      // If no custom properties elements, we're done (just color swatches and dropdown)
      if (!this.elements.brand) {
        console.log('No custom properties elements found - color swatches and dropdown only');
        
        // Debug: Check the current pathname
        console.log('Current pathname:', window.location.pathname);
        console.log('Does it include "one-length-new"?', window.location.pathname.includes('one-length-new'));
        
        // For one-length-new template, add base price label
        const isOneLengthNewTemplate = window.location.pathname.includes('one-length-new');
        if (isOneLengthNewTemplate) {
          console.log('Adding base price label for one-length-new template...');
          this.addBasePriceLabel();
        } else {
          console.log('Not one-length-new template, skipping base price label');
        }
        
        // Still need to set up select all checkbox if it exists
        if (this.elements.selectAllClubs) {
          console.log('✅ Setting up select all checkbox for template without custom properties');
          this.elements.selectAllClubs.addEventListener('change', (e) => {
            this.handleSelectAllChange(e.target.checked);
          });
          this.updateSelectAllState();
        }
        
        // Try to find and initialize color dropdown with a delay (in case DOM isn't fully ready)
        setTimeout(() => {
          console.log('🔍 Delayed attempt to find color dropdown...');
          const colorDropdown = document.getElementById('properties_color') || 
                               document.querySelector('[id^="properties_color_"]') || 
                               document.querySelector('select[name="properties[Color]"]');
          
          if (colorDropdown && !this.elements.color) {
            console.log('✅ Found color dropdown on delayed attempt:', colorDropdown);
            this.elements.color = colorDropdown;
            this.initializeColorOptions(colorData);
            colorDropdown.addEventListener('change', (e) => this.handleColorDropdownChange(e));
          } else if (colorDropdown) {
            console.log('✅ Color dropdown was already initialized');
          } else {
            console.log('❌ Still no color dropdown found on delayed attempt');
          }
        }, 100);
        
        // Set up form interception for one-length templates
        console.log('🔧 Setting up form interception for one-length template...');
        const productForm = document.querySelector('[data-type="add-to-cart-form"]');
        if (productForm) {
          console.log('✅ Found product form, adding submit listener');
          productForm.addEventListener('submit', async (event) => {
            console.log('🎯 FORM SUBMIT INTERCEPTED');
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            
            await this.addOneLengthTierProductToCart();
            return false;
          }, true); // Use capture phase
        } else {
          console.error('❌ Could not find product form');
        }
        
        // Also set up button interception as backup
        this.setupButtonInterception();
        
        return;
      }
      
      // Initialize custom properties functionality
      console.log('Custom properties found - initializing full functionality');
      this.bindEvents();
      this.initializeBrandOptions();
      this.initializeColorOptions(colorData); // This will run again but that's OK
      this.setupFormValidation();
      this.updateSelectAllState();
      this.updatePrice(); // Initialize price calculation
      
      // For one-length-new template, add base price label
      const isOneLengthNewTemplate = window.location.pathname.includes('one-length-new');
      if (isOneLengthNewTemplate) {
        console.log('Adding base price label for one-length-new template...');
        this.addBasePriceLabel();
      }
    } catch (error) {
      console.error('Failed to initialize custom line item properties:', error);
      // Don't rethrow - let the page continue to work even if our script fails
    }
  }

  async loadTierProductsData() {
    try {
      console.log('Loading tier products data from JSON file...');
      
      // Use the properly generated asset URL from Liquid with cache-busting
      const tierProductsUrl = window.tierProductsUrl || '/assets/tier-products.json';
      console.log('Fetching tier products data from:', tierProductsUrl);
      
      const response = await fetch(tierProductsUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Tier products data loaded successfully:', data);
      return data;
    } catch (error) {
      console.error('Failed to load tier products data, using fallback:', error);
      // Return a basic fallback structure
      return {
        tierProducts: {
          tier1: {
            productId: null,
            title: "Golf Club Wrapping - Tier 1",
            multiplier: 1.0,
            colors: ["black", "white", "navy", "red"],
            variants: {}
          }
        }
      };
    }
  }

  async loadData() {
    try {
      console.log('Loading golf club data from JSON file...');
      
      // Use the properly generated asset URL from Liquid with cache-busting
      const golfClubDataUrl = window.golfClubDataUrl || '/assets/golf-club-data.json';
      console.log('Fetching golf club data from:', golfClubDataUrl);
      
      const response = await fetch(golfClubDataUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      this.data = await response.json();
      console.log('Golf club data loaded successfully:', this.data);
    } catch (error) {
      console.error('Failed to load golf club data, using fallback:', error);
      // Fallback to embedded data if JSON file fails to load
      this.data = {
        "brands": {
          "titleist": {
            "name": "Titleist",
            "collections": {
              "t_series": {
                "name": "T-Series",
                "models": {
                  "t100": {
                    "name": "T100",
                    "years": ["2019", "2020", "2021", "2022", "2023", "2024"]
                  },
                  "t200": {
                    "name": "T200",
                    "years": ["2019", "2020", "2021", "2022", "2023", "2024"]
                  },
                  "t300": {
                    "name": "T300",
                    "years": ["2021", "2022", "2023", "2024"]
                  }
                }
              }
            }
          },
          "callaway": {
            "name": "Callaway",
            "collections": {
              "apex": {
                "name": "Apex",
                "models": {
                  "apex_pro": {
                    "name": "Apex Pro",
                    "years": ["2019", "2020", "2021", "2022", "2023", "2024"]
                  },
                  "apex_dcb": {
                    "name": "Apex DCB",
                    "years": ["2021", "2022", "2023", "2024"]
                  }
                }
              }
            }
          },
          "taylormade": {
            "name": "TaylorMade",
            "collections": {
              "p_series": {
                "name": "P Series",
                "models": {
                  "p770": {
                    "name": "P770",
                    "years": ["2019", "2020", "2021", "2022", "2023", "2024"]
                  },
                  "p790": {
                    "name": "P790",
                    "years": ["2019", "2020", "2021", "2022", "2023", "2024"]
                  }
                }
              }
            }
          }
        }
      };
      console.log('Using fallback golf club data');
    }
  }

  async loadColorData() {
    try {
      console.log('Loading color data from JSON file...');
      
      // Use the properly generated asset URL from Liquid with cache-busting
      const colorOptionsUrl = window.colorOptionsUrl || '/assets/color-options.json';
      console.log('Fetching color data from:', colorOptionsUrl);
      
      const response = await fetch(colorOptionsUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const colorData = await response.json();
      console.log('Color data loaded successfully:', colorData);
      return colorData;
    } catch (error) {
      console.error('Failed to load color data, using fallback:', error);
      // Fallback color data if JSON file fails to load
      return {
        "colors": {
          "black": {
            "name": "Black",
            "swatch": "swatch_black.jpg",
            "productImage": "product_black.jpg"
          },
          "carbon-fiber": {
            "name": "Carbon Fiber",
            "swatch": "swatch_carbonfiber.jpg",
            "productImage": "product_carbonfiber.jpg"
          },
          "red-black-iridescent": {
            "name": "Red Black Iridescent", 
            "swatch": "swatch_red_black_iridescent.jpg",
            "productImage": "product_redblackiridescent.jpg"
          },
          "pearl": {
            "name": "Pearl",
            "swatch": "swatch_pearl.jpg",
            "productImage": "product_pearl.jpg"
          }
        }
      };
    }
  }

  bindElements() {
    console.log('Binding elements...');
    
    // Always try to bind color swatch container (for all products now)
    const colorSwatchContainer = document.getElementById('color-swatches-container');
    
    // Check if custom properties elements exist (using correct case)
    const hasCustomProperties = document.getElementById('properties_brand');
    
    if (!hasCustomProperties) {
      console.log('No custom properties found - setting elements for color swatches only');
      
      // Try multiple approaches to find the color dropdown
      let colorDropdown = null;
      const colorSelectors = [
        'properties_color',
        '[id^="properties_color_"]',
        'select[name="properties[Color]"]',
        '.color-selector select',
        '.color-selector__select'
      ];

      for (const selector of colorSelectors) {
        let element;
        if (selector === 'properties_color') {
          element = document.getElementById(selector);
        } else {
          element = document.querySelector(selector);
        }
        
        if (element) {
          console.log(`✅ Found color dropdown using selector "${selector}":`, element);
          colorDropdown = element;
          break;
        } else {
          console.log(`❌ No element found for selector "${selector}"`);
        }
      }
      
      this.elements = {
        brand: null,
        collection: null,
        model: null,
        year: null,
        color: colorDropdown,
        shaftLength: null,
        gripLength: null,
        form: null,
        addToCartButton: null,
        colorSwatchContainer: colorSwatchContainer
      };
      return;
    }
    
    // Only bind elements if custom properties exist
    this.elements = {
      brand: document.getElementById('properties_brand'),
      collection: document.getElementById('properties_collection'),
      model: document.getElementById('properties_model'),
      year: document.getElementById('properties_year'),
      color: document.getElementById('properties_color') || document.querySelector('[id^="properties_color_"]') || document.querySelector('select[name="properties[Color]"]'),
      shaftLength: document.getElementById('properties_shaft_length'),
      gripLength: document.getElementById('properties_grip_length'),
      selectedClubsContainer: document.getElementById('selected_clubs_container'),
      selectedClubsCombined: document.getElementById('properties_selected_clubs_combined'),
      selectAllClubs: document.getElementById('select_all_clubs'),
      calculatedTotal: document.getElementById('properties_calculated_total'),
      clubCount: document.getElementById('properties_club_count'),
      colorMultiplier: document.getElementById('properties_color_multiplier'),
      additionalCost: document.getElementById('properties_additional_cost'),
      form: document.querySelector('[data-type="add-to-cart-form"]'),
      addToCartButton: document.querySelector('.product-form__submit'),
      colorSwatchContainer: document.getElementById('color-swatches-container') || document.getElementById('color-selector-swatches-container'),
      priceContainer: document.querySelector('.price-item--regular'),
      priceElement: document.querySelector('.price .price-item--regular'),
      checkoutPriceElement: document.querySelector('.price-checkout .price-item--regular')
    };
    console.log('Elements found:', this.elements);
    
    // Additional debugging for color element specifically
    console.log('Color element debugging:');
    console.log('- getElementById("properties_color"):', document.getElementById('properties_color'));
    console.log('- querySelector("[id^=\'properties_color_\']"):', document.querySelector('[id^="properties_color_"]'));
    console.log('- querySelector("select[name=\'properties[Color]\']"):', document.querySelector('select[name="properties[Color]"]'));
    console.log('- Final color element:', this.elements.color);
    
    // Additional debugging - check for alternative selectors if main ones fail
    if (!this.elements.form) {
      console.log('Main form not found, trying alternative selectors...');
      this.elements.form = document.querySelector('form[id*="product-form"]');
      console.log('Alternative form found:', this.elements.form);
    }
    
    if (!this.elements.addToCartButton) {
      console.log('Main add to cart button not found, trying alternative selectors...');
      this.elements.addToCartButton = document.querySelector('button[type="submit"][name="add"]') || 
                                     document.querySelector('button[id*="AddToCart"]') ||
                                     document.querySelector('.cpb-personalize-btn');
      console.log('Alternative button found:', this.elements.addToCartButton);
    }
  }

  bindEvents() {
    if (this.elements.brand) {
      this.elements.brand.addEventListener('change', (e) => this.handleBrandChange(e));
    }
    if (this.elements.collection) {
      this.elements.collection.addEventListener('change', (e) => this.handleCollectionChange(e));
    }
    if (this.elements.model) {
      this.elements.model.addEventListener('change', (e) => this.handleModelChange(e));
    }
    if (this.elements.year) {
      this.elements.year.addEventListener('change', (e) => this.validateForm());
    }
    if (this.elements.color) {
      this.elements.color.addEventListener('change', (e) => this.handleColorDropdownChange(e));
    }
    
    // Add event listeners to selected clubs checkboxes
    const clubCheckboxes = document.querySelectorAll('.club-checkbox');
    clubCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateSelectedClubsValue();
        this.updateSelectAllState();
        this.updatePrice();
        this.validateForm();
      });
    });
    
    // Add event listener for Select All checkbox
    if (this.elements.selectAllClubs) {
      this.elements.selectAllClubs.addEventListener('change', (e) => {
        this.handleSelectAllChange(e.target.checked);
      });
    }
    
    // Add event listener for color changes
    if (this.elements.color) {
      this.elements.color.addEventListener('change', () => {
        this.updatePrice();
      });
    }
  }

  setupFormValidation() {
    console.log('Setting up form validation...');
    console.log('Form element:', this.elements.form);
    console.log('Button element:', this.elements.addToCartButton);
    
    // Only set up validation if custom properties elements actually exist
    if (!this.elements.brand) {
      console.log('No custom properties found - skipping validation setup');
      return;
    }
    
    // Strategy: Intercept at the button level before form submission
    if (this.elements.addToCartButton) {
      this.setupButtonInterception();
      console.log('Button interception validation set up successfully');
    } else {
      console.warn('Could not set up form validation - missing button element');
    }
  }

  setupButtonInterception() {
    console.log('🔧 setupButtonInterception called');
    console.log('🔧 Add to cart button element:', this.elements.addToCartButton);
    
    if (!this.elements.addToCartButton) {
      console.error('❌ No add to cart button found, cannot set up interception');
      return;
    }
    
    try {
      console.log('✅ Setting up button click listener...');
      // Replace the button's click handler with our validation
      this.elements.addToCartButton.addEventListener('click', async (event) => {
        try {
          console.log('🎯 Button click intercepted for validation');
          
          // Check if this is a one-length template (has size selector but no custom properties)
          const isOneLengthTemplate = document.querySelector('input[name="properties[Size]"]') !== null;
          
          if (isOneLengthTemplate) {
            console.log('One-length template detected, handling tier product addition');
            event.preventDefault();
            event.stopPropagation();
            await this.addOneLengthTierProductToCart();
            return false;
          }
          
          // Only validate if custom properties exist (default template)
          if (!this.elements.brand) {
            console.log('No custom properties found, allowing normal cart submission');
            return true; // Let the normal form submission proceed
          }

          // Validate our custom properties first
          if (!this.validateCustomProperties()) {
            console.log('Custom validation failed, preventing submission');
            event.preventDefault();
            event.stopPropagation();
            this.showValidationError();
            return false;
          }

          // Prevent default form submission
          event.preventDefault();
          event.stopPropagation();

          // Add tier product to cart instead (only for default template with custom properties)
          await this.addTierProductToCart();
          
          return false; // Always prevent default since we handle it ourselves
        } catch (error) {
          console.error('Error in custom validation click handler:', error);
          // If our validation fails, allow the submission to proceed
          return true;
        }
      }, { capture: true }); // Use capture to run before other handlers
    } catch (error) {
      console.error('Error setting up button interception:', error);
    }
  }

  async addOneLengthTierProductToCart() {
    try {
      console.log('🎯 Adding one-length tier product to cart');
      
      // Get current selections
      const selections = this.getCurrentSelections();
      console.log('✅ Current selections:', selections);
      console.log('✅ Quantity value:', selections.quantity);
      console.log('✅ Size value:', selections.size);
      console.log('✅ Color value:', selections.color);
      console.log('✅ Color multiplier:', selections.colorMultiplier);
      
      // Validate that a quantity is selected
      if (!selections.quantity || selections.quantity < 1) {
        console.error('❌ Validation failed: Invalid quantity', selections.quantity);
        throw new Error('Please select a quantity');
      }
      console.log('✅ Quantity validation passed');
      
      // Validate that required fields are selected
      if (!selections.size) {
        throw new Error('Please select a size (Adult/Child)');
      }
      
      if (!selections.color) {
        throw new Error('Please select a color');
      }
      
      console.log('✅ Size validation passed');
      
      if (!selections.color) {
        console.error('❌ Validation failed: No color selected');
        throw new Error('Please select a color');
      }
      console.log('✅ Color validation passed');
      
      // Find the appropriate tier product
      console.log('🔍 Finding tier product...');
      const tierProduct = this.findTierProduct(selections);
      console.log('🔍 Tier product found:', tierProduct ? tierProduct.title : 'NONE');
      
      if (!tierProduct) {
        console.error('❌ Could not find tier product for selections:', selections);
        console.error('Available tier products:', this.tierProducts?.tierProducts ? Object.keys(this.tierProducts.tierProducts) : 'NONE');
        throw new Error('Could not find matching tier product');
      }
      
      // Get the variant for this quantity
      const variantKey = selections.quantity.toString();
      console.log('🔍 Looking for variant key:', variantKey);
      console.log('🔍 Available variants:', tierProduct.variants ? Object.keys(tierProduct.variants) : 'NONE');
      const variantData = tierProduct.variants[variantKey];
      console.log('🔍 Variant data:', variantData);
      
      if (!variantData || !variantData.variantId) {
        console.error('❌ No variant found for quantity:', selections.quantity);
        console.error('Available variant keys:', Object.keys(tierProduct.variants || {}));
        throw new Error(`No variant found for ${selections.quantity} clubs`);
      }
      console.log('✅ Variant found:', variantData.variantId);
      
      console.log(`🛒 Adding to cart:`, {
        tierProduct: tierProduct.title,
        variantId: variantData.variantId,
        quantity: selections.quantity,
        size: selections.size,
        color: selections.color,
        price: variantData.price
      });
      
      // Show loading state
      this.showLoadingState();
      
      // Prepare form data
      const formData = new FormData();
      formData.append('id', variantData.variantId);
      formData.append('quantity', '1');
      formData.append('properties[Size]', selections.size);
      formData.append('properties[Color]', selections.color);
      formData.append('properties[Club Count]', selections.quantity.toString());
      
      console.log('📦 FormData being sent to cart:');
      for (let [key, value] of formData.entries()) {
        console.log(`   ${key}: ${value}`);
      }
      
      // Add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      console.log('🌐 Cart API Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.log('❌ Cart API Error Response:', errorData);
        throw new Error(`Failed to add to cart: ${errorData}`);
      }
      
      const result = await response.json();
      console.log('✅ Successfully added to cart:', result);
      
      // Show success and redirect
      this.showSuccessState();
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);
      
    } catch (error) {
      console.error('Error adding one-length tier product to cart:', error);
      this.showErrorState(error.message);
    }
  }

  async addTierProductToCart() {
    try {
      // Safety check: this method should only be called for default template with custom properties
      if (!this.elements.brand || !this.elements.collection || !this.elements.model) {
        console.error('addTierProductToCart called but missing required custom property elements');
        throw new Error('This method requires custom property elements (default template)');
      }

      // Show loading state
      this.showLoadingState();

      // Get selected clubs count
      const selectedClubs = document.querySelectorAll('.club-checkbox:checked');
      const clubCount = selectedClubs.length;
      const selectedClubsList = Array.from(selectedClubs).map(cb => cb.dataset.clubValue).join(', ');

      // Determine which tier based on selected color
      const selectedColor = this.elements.color.value;
      const tier = this.getColorTier(selectedColor);
      
      if (!tier) {
        throw new Error('Could not determine color tier');
      }

      // Get the correct variant for this club count
      const variantData = this.tierProducts.tierProducts[tier].variants[clubCount.toString()];
      
      if (!variantData || !variantData.variantId) {
        throw new Error(`No variant found for ${clubCount} clubs in ${tier}`);
      }

      console.log(`Adding to cart: ${tier} with ${clubCount} clubs, variant ID: ${variantData.variantId}`);

      // Prepare customization data as line item properties
      const customData = {
        'Selected Clubs': selectedClubsList,
        'Club Count': clubCount.toString(),
        'Color': selectedColor,
        'Brand': this.elements.brand.value,
        'Collection': this.elements.collection.value,
        'Model': this.elements.model.value,
        'Year': this.elements.year.value,
        'Shaft Length': this.elements.shaftLength.value,
        'Grip Length': this.elements.gripLength.value,
        'Tier': tier,
        'Calculated Total': variantData.price.toFixed(2)
      };

      console.log('🛒 Adding to cart with details:');
      console.log('   - Product ID:', this.tierProducts.tierProducts[tier].productId);
      console.log('   - Variant ID:', variantData.variantId);
      console.log('   - Club Count:', clubCount);
      console.log('   - Price:', variantData.price);
      console.log('   - Tier:', tier);
      console.log('   - Color:', selectedColor);

      // Add to cart using Shopify's cart API
      const formData = new FormData();
      formData.append('id', variantData.variantId);
      formData.append('quantity', '1');
      
      // Add all customization data as properties
      Object.keys(customData).forEach(key => {
        if (customData[key]) {
          formData.append(`properties[${key}]`, customData[key]);
        }
      });

      console.log('📦 FormData being sent to cart:');
      for (let [key, value] of formData.entries()) {
        console.log(`   ${key}: ${value}`);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      console.log('🌐 Cart API Response Status:', response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.log('❌ Cart API Error Response:', errorData);
        throw new Error(`Failed to add to cart: ${errorData}`);
      }

      const result = await response.json();
      console.log('Added to cart:', result);

      // Show success and redirect
      this.showSuccessState();
      
      // Optional: redirect to cart after short delay
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Error adding tier product to cart:', error);
      this.showErrorState(error.message);
    }
  }

  // Maps a color multiplier to its tier number. Sale colors (0.8) are tier 5.
  // This mapping is the single source of truth shared by the swatch display,
  // the price labels, and the add-to-cart routing, so the tier a shopper sees
  // always matches the product that gets added. Returns null for unknown
  // multipliers so callers can decide their own fallback.
  getTierNumberForMultiplier(multiplier) {
    const map = { 0.8: 5, 1: 1, 1.2: 2, 1.4: 3, 1.6: 4 };
    return map[multiplier] || null;
  }

  // Tier 5 is the Sale tier and is shown as "Sale" instead of a tier number.
  getTierLabel(tierNumber) {
    return tierNumber === 5 ? 'Sale' : `Tier ${tierNumber}`;
  }

  getColorTier(colorValue) {
    // Check each tier to see which contains this color
    for (const [tierKey, tierData] of Object.entries(this.tierProducts.tierProducts)) {
      // Only check colors array if it exists (default template tiers have this)
      if (tierData.colors && tierData.colors.includes(colorValue)) {
        return tierKey;
      }
    }

    // Fallback: check color data for multiplier and map to tier
    if (this.colorData?.colors && this.colorData.colors[colorValue]) {
      const tierNumber = this.getTierNumberForMultiplier(this.colorData.colors[colorValue].multiplier);
      if (tierNumber) return `tier${tierNumber}`;
    }

    return null;
  }

  showLoadingState() {
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = true;
      this.elements.addToCartButton.textContent = 'Creating Custom Product...';
    }
  }

  showSuccessState() {
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.textContent = 'Added to Cart!';
      this.elements.addToCartButton.style.backgroundColor = '#4CAF50';
    }
  }

  showErrorState(message) {
    if (this.elements.addToCartButton) {
      this.elements.addToCartButton.disabled = false;
      this.elements.addToCartButton.textContent = 'Add to Cart';
    }
    
    // Show error message
    this.showValidationError(`Error: ${message}`);
  }

  validateCustomProperties() {
    try {
      // All required fields for golf club customization
      const requiredFields = [
        { element: this.elements.color, name: 'Color' },
        { element: this.elements.brand, name: 'Brand' },
        { element: this.elements.collection, name: 'Collection' },
        { element: this.elements.model, name: 'Model' },
        { element: this.elements.year, name: 'Year' },
        { element: this.elements.shaftLength, name: 'Shaft Length' },
        { element: this.elements.gripLength, name: 'Grip Length' }
      ];

      console.log('🔍 Starting validation debug...');
      console.log('Available elements:', this.elements);
      
      let allValid = true;
      const invalidFields = [];

      for (const field of requiredFields) {
        const hasElement = !!field.element;
        const hasValue = field.element && field.element.value && field.element.value.trim() !== '';
        
        console.log(`📋 ${field.name}:`);
        console.log(`   - Element exists: ${hasElement}`);
        console.log(`   - Element:`, field.element);
        console.log(`   - Value: "${field.element ? field.element.value : 'N/A'}"`);
        console.log(`   - Valid: ${hasElement && hasValue}`);
        
        if (!hasElement || !hasValue) {
          allValid = false;
          invalidFields.push(field.name);
          console.log(`❌ ${field.name} validation failed`);
        } else {
          console.log(`✅ ${field.name} validation passed`);
        }
      }

      // Validate selected clubs checkboxes
      const selectedClubsCheckboxes = document.querySelectorAll('.club-checkbox:checked');
      console.log('🏌️ Selected clubs count:', selectedClubsCheckboxes.length);
      console.log('🏌️ Selected clubs:', Array.from(selectedClubsCheckboxes).map(cb => cb.value));
      
      if (selectedClubsCheckboxes.length === 0) {
        allValid = false;
        invalidFields.push('Club Selection');
        console.log('❌ No clubs selected');
      } else {
        console.log('✅ Clubs selected');
      }

      console.log('📊 Validation Summary:');
      console.log(`   - All valid: ${allValid}`);
      console.log(`   - Invalid fields: [${invalidFields.join(', ')}]`);

      return allValid;
    } catch (error) {
      console.error('💥 Error in validateCustomProperties:', error);
      // If validation fails due to error, allow submission
      return true;
    }
  }

  showValidationError() {
    // Create or update error message
    let errorDiv = document.querySelector('.custom-properties-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'custom-properties-error';
      
      // Set styles individually to be CSP-compliant
      errorDiv.style.color = 'rgb(var(--color-error))';
      errorDiv.style.backgroundColor = 'rgb(var(--color-error) / 0.1)';
      errorDiv.style.border = '1px solid rgb(var(--color-error) / 0.3)';
      errorDiv.style.borderRadius = '0.3rem';
      errorDiv.style.padding = '1rem';
      errorDiv.style.margin = '1rem 0';
      errorDiv.style.fontSize = '1.4rem';
      
      const propertiesContainer = document.querySelector('.custom-line-item-properties');
      if (propertiesContainer) {
        propertiesContainer.insertAdjacentElement('afterend', errorDiv);
      }
    }
    
    errorDiv.textContent = 'Please select all required fields (Color, Brand, Collection, Model, Year, Shaft Length, Grip Length) and at least one club before adding to cart.';
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    // Remove error after 5 seconds
    setTimeout(() => {
      if (errorDiv && errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  initializeBrandOptions() {
    console.log('Initializing brand options...');
    if (!this.elements.brand || !this.data) {
      console.error('Missing brand element or data:', { 
        brandElement: this.elements.brand, 
        data: this.data 
      });
      return;
    }

    // Clear existing options except the first one
    this.elements.brand.innerHTML = '<option value="">Select a Brand</option>';

    // Add brand options
    Object.keys(this.data.brands).forEach(brandKey => {
      const brand = this.data.brands[brandKey];
      const option = document.createElement('option');
      option.value = brandKey;
      option.textContent = brand.name;
      this.elements.brand.appendChild(option);
      console.log('Added brand option:', brand.name);
    });
    
    console.log('Brand initialization complete');
  }

  handleBrandChange(event) {
    const selectedBrand = event.target.value;
    this.clearDownstreamSelects(['collection', 'model', 'year']);

    if (!selectedBrand || !this.data.brands[selectedBrand]) {
      this.disableSelects(['collection', 'model', 'year']);
      return;
    }

    this.populateCollections(selectedBrand);
    this.enableSelect('collection');
    this.validateForm();
  }

  handleCollectionChange(event) {
    const selectedCollection = event.target.value;
    const selectedBrand = this.elements.brand.value;
    this.clearDownstreamSelects(['model', 'year']);

    if (!selectedCollection || !selectedBrand || !this.data.brands[selectedBrand].collections[selectedCollection]) {
      this.disableSelects(['model', 'year']);
      return;
    }

    this.populateModels(selectedBrand, selectedCollection);
    this.enableSelect('model');
    this.validateForm();
  }

  handleModelChange(event) {
    const selectedModel = event.target.value;
    const selectedBrand = this.elements.brand.value;
    const selectedCollection = this.elements.collection.value;
    this.clearDownstreamSelects(['year']);

    if (!selectedModel || !selectedBrand || !selectedCollection || 
        !this.data.brands[selectedBrand].collections[selectedCollection].models[selectedModel]) {
      this.disableSelects(['year']);
      return;
    }

    this.populateYears(selectedBrand, selectedCollection, selectedModel);
    this.enableSelect('year');
    this.validateForm();
  }

  validateForm() {
    // Remove any existing error messages when form becomes valid
    const errorDiv = document.querySelector('.custom-properties-error');
    if (errorDiv && this.validateCustomProperties()) {
      errorDiv.parentNode.removeChild(errorDiv);
    }
  }

  populateCollections(brandKey) {
    if (!this.elements.collection || !this.data.brands[brandKey]) return;

    this.elements.collection.innerHTML = '<option value="">Select a Collection</option>';
    
    Object.keys(this.data.brands[brandKey].collections).forEach(collectionKey => {
      const collection = this.data.brands[brandKey].collections[collectionKey];
      const option = document.createElement('option');
      option.value = collectionKey;
      option.textContent = collection.name;
      this.elements.collection.appendChild(option);
    });
  }

  populateModels(brandKey, collectionKey) {
    if (!this.elements.model || !this.data.brands[brandKey].collections[collectionKey]) return;

    this.elements.model.innerHTML = '<option value="">Select a Model</option>';
    
    Object.keys(this.data.brands[brandKey].collections[collectionKey].models).forEach(modelKey => {
      const model = this.data.brands[brandKey].collections[collectionKey].models[modelKey];
      const option = document.createElement('option');
      option.value = modelKey;
      option.textContent = model.name;
      this.elements.model.appendChild(option);
    });
  }

  populateYears(brandKey, collectionKey, modelKey) {
    if (!this.elements.year || !this.data.brands[brandKey].collections[collectionKey].models[modelKey]) return;

    this.elements.year.innerHTML = '<option value="">Select a Year</option>';
    
    const years = this.data.brands[brandKey].collections[collectionKey].models[modelKey].years;
    years.forEach(year => {
      const option = document.createElement('option');
      option.value = year;
      option.textContent = year;
      this.elements.year.appendChild(option);
    });
  }

  clearDownstreamSelects(selectNames) {
    selectNames.forEach(name => {
      if (this.elements[name]) {
        this.elements[name].innerHTML = `<option value="">Select a ${name.charAt(0).toUpperCase() + name.slice(1)}</option>`;
      }
    });
  }

  disableSelects(selectNames) {
    selectNames.forEach(name => {
      if (this.elements[name]) {
        this.elements[name].disabled = true;
      }
    });
  }

  enableSelect(selectName) {
    if (this.elements[selectName]) {
      this.elements[selectName].disabled = false;
    }
  }

  initializeColorOptions(colorData) {
    console.log('=== INITIALIZING COLOR DROPDOWN OPTIONS ===');
    console.log('Color element found:', !!this.elements.color);
    console.log('Color element details:', this.elements.color);
    console.log('Color data available:', !!colorData?.colors);
    console.log('Color data keys:', colorData?.colors ? Object.keys(colorData.colors) : 'none');
    
    if (!this.elements.color || !colorData?.colors) {
      console.log('❌ Missing color element or data - skipping color dropdown initialization:', { 
        colorElement: !!this.elements.color, 
        colorData: !!colorData?.colors 
      });
      return;
    }

    console.log('✅ Proceeding with color dropdown initialization...');
    
    // Clear existing options
    this.elements.color.innerHTML = '<option value="">Select a Color</option>';
    console.log('✅ Cleared existing options and set default');

    // Add color options
    Object.keys(colorData.colors).forEach(colorKey => {
      const color = colorData.colors[colorKey];
      const option = document.createElement('option');
      option.value = colorKey;
      option.textContent = color.name;
      this.elements.color.appendChild(option);
      console.log('✅ Added color option:', color.name, 'with key:', colorKey);
    });
    
    console.log('✅ Color dropdown initialization complete. Total options:', this.elements.color.options.length);
  }

  initializeColorSwatches(colorData) {
    console.log('🎨 Initializing color swatches...');
    console.log('🎨 Color swatch container element:', this.elements.colorSwatchContainer);
    console.log('🎨 Color data:', colorData);
    console.log('🎨 Asset URL base:', window.shopifyAssetUrlBase);
    console.log('🎨 getAssetUrl function available:', typeof window.getAssetUrl);
    
    if (!this.elements.colorSwatchContainer || !colorData?.colors) {
      console.log('🎨 Color swatch container not found or no color data - skipping swatch initialization');
      console.log('🎨 Container exists:', !!this.elements.colorSwatchContainer);
      console.log('🎨 Color data exists:', !!colorData?.colors);
      
      // Let's check what containers we do have available
      const mainContainer = document.getElementById('color-swatches-container');
      const selectorContainer = document.getElementById('color-selector-swatches-container');
      console.log('🎨 Main container found:', !!mainContainer);
      console.log('🎨 Selector container found:', !!selectorContainer);
      return;
    }

    console.log('🎨 Proceeding with color swatch initialization...');

    // Clear existing swatches
    this.elements.colorSwatchContainer.innerHTML = '';

    // Group colors by multiplier
    const colorsByMultiplier = {};
    Object.keys(colorData.colors).forEach(colorKey => {
      const color = colorData.colors[colorKey];
      const multiplier = color.multiplier || 1;
      
      if (!colorsByMultiplier[multiplier]) {
        colorsByMultiplier[multiplier] = [];
      }
      colorsByMultiplier[multiplier].push({ key: colorKey, ...color });
    });

    // Sort multipliers to ensure consistent tier ordering (base tier first, then premium)
    const sortedMultipliers = Object.keys(colorsByMultiplier)
      .map(m => parseFloat(m))
      .sort((a, b) => a - b);

    console.log('Color tiers by multiplier:', colorsByMultiplier);

    // Create tiers for each multiplier
    sortedMultipliers.forEach((multiplier, index) => {
      const colors = colorsByMultiplier[multiplier];
      
      // Create tier container
      const tierContainer = document.createElement('div');
      tierContainer.className = 'color-tier';
      tierContainer.setAttribute('data-multiplier', multiplier);
      
      // Create tier header
      const tierHeader = document.createElement('div');
      tierHeader.className = 'color-tier-header';
      
      const tierTitle = document.createElement('h4');
      tierTitle.className = 'color-tier-title';
      
      // Resolve the tier number from the multiplier (not list position) so the
      // label and price stay aligned even when the Sale tier (0.8) is present.
      const tierNumber = this.getTierNumberForMultiplier(multiplier) || (index + 1);

      // Get the correct tier price based on template and age group
      let tierPrice = '7.00'; // fallback
      
      try {
        // Determine template type by checking for size selector elements (only exists in one-length template)
        const isOneLengthTemplate = document.querySelector('input[name="properties[Size]"]') ||
                                   document.querySelector('.size-selector') ||
                                   window.location.pathname.includes('one-length');
        
        // Get current size selection (default to Adult for pricing display)
        const selectedSize = this.getCurrentSize() || 'Adult';
        
        if (isOneLengthTemplate) {
          // One-length template
          const ageGroup = selectedSize === 'Child' ? 'Child' : 'Adult';
          const tierKey = `oneLength${ageGroup}Tier${tierNumber}`;
          
          if (this.tierProducts?.tierProducts?.[tierKey]?.tierPricePerClub) {
            tierPrice = this.tierProducts.tierProducts[tierKey].tierPricePerClub.toFixed(2);
          }
        } else {
          // Default template
          const tierKey = `tier${tierNumber}`;
          
          if (this.tierProducts?.tierProducts?.[tierKey]?.tierPricePerClub) {
            tierPrice = this.tierProducts.tierProducts[tierKey].tierPricePerClub.toFixed(2);
          }
        }
        
        console.log(`${this.getTierLabel(tierNumber)} price: $${tierPrice}/club (template: ${isOneLengthTemplate ? 'one-length' : 'default'}, size: ${selectedSize})`);
      } catch (error) {
        console.error('Error calculating tier price:', error);
      }
      
      tierTitle.textContent = `${this.getTierLabel(tierNumber)} - $${tierPrice}/club`;

      tierHeader.appendChild(tierTitle);
      tierContainer.appendChild(tierHeader);
      
      // Create swatches container for this tier
      const tierSwatches = document.createElement('div');
      tierSwatches.className = 'color-tier-swatches';
      
      // Create swatches for colors in this tier
      colors.forEach(color => {
        const swatchWrapper = document.createElement('div');
        swatchWrapper.className = 'color-swatch-wrapper';
        swatchWrapper.setAttribute('data-color-key', color.key);
        swatchWrapper.setAttribute('data-multiplier', multiplier);
        
        const swatchImage = document.createElement('img');
        // Use proper Shopify asset URL for swatch images
        swatchImage.src = window.getAssetUrl ? window.getAssetUrl(color.swatch) : `/assets/${color.swatch}`;
        console.log('🖼️ Setting swatch image src to:', swatchImage.src);
        swatchImage.alt = `${color.name} color swatch - Click to select this color`;
        swatchImage.title = `Select ${color.name}`;
        swatchImage.className = 'color-swatch-image';
        swatchImage.setAttribute('data-color-key', color.key);
        swatchImage.setAttribute('data-color-name', color.name);
        swatchImage.setAttribute('data-product-image', color.productImage);
        
        const swatchLabel = document.createElement('span');
        swatchLabel.textContent = color.name;
        swatchLabel.className = 'color-swatch-label';
        
        swatchWrapper.appendChild(swatchImage);
        swatchWrapper.appendChild(swatchLabel);
        tierSwatches.appendChild(swatchWrapper);
        
        // Add click event to both image and wrapper for better usability
        const clickHandler = () => {
          this.handleColorSwatchClick(color.key, color.name, color.productImage);
        };
        
        swatchImage.addEventListener('click', clickHandler);
        swatchWrapper.addEventListener('click', clickHandler);
        
        console.log('Added color swatch:', color.name, 'to tier', multiplier);
      });
      
      tierContainer.appendChild(tierSwatches);
      this.elements.colorSwatchContainer.appendChild(tierContainer);
    });
  }

  initializeSizeSelector() {
    console.log('🔧 Initializing size selector with Adult as default...');
    
    // Find all size radio buttons
    const adultSizeInputs = document.querySelectorAll('input[name="properties[Size]"][value="Adult"]');
    const childSizeInputs = document.querySelectorAll('input[name="properties[Size]"][value="Child"]');
    
    console.log('Found Adult size inputs:', adultSizeInputs.length);
    console.log('Found Child size inputs:', childSizeInputs.length);
    
    // Ensure Adult is selected and Child is not
    adultSizeInputs.forEach((input, index) => {
      input.checked = true;
      console.log(`✅ Set Adult size input ${index + 1} to checked`);
    });
    
    childSizeInputs.forEach((input, index) => {
      input.checked = false;
      console.log(`❌ Set Child size input ${index + 1} to unchecked`);
    });
    
    // Also add event listeners to track size changes
    const allSizeInputs = document.querySelectorAll('input[name="properties[Size]"]');
    allSizeInputs.forEach(input => {
      input.addEventListener('change', (e) => {
        console.log('Size changed to:', e.target.value);
        // Update price when size changes (may affect tier product selection)
        this.updatePrice();
        // Update tier pricing labels when size changes
        this.updateTierPricingLabels();
      });
    });
    
    console.log('✅ Size selector initialization complete');
  }

  setupPriceUpdateTriggers() {
    console.log('🔗 Setting up price update triggers...');
    
    // Color selection triggers (swatches and dropdown)
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="properties[Color]"]') || 
          e.target.matches('select[name="properties[Color]"]') ||
          e.target.id && e.target.id.startsWith('properties_color')) {
        console.log('Color selection changed, updating price');
        this.updatePrice();
      }
    });
    
    // Size selection triggers
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="properties[Size]"]')) {
        console.log('Size selection changed, updating price');
        this.updatePrice();
        // Update tier pricing labels when size changes
        this.updateTierPricingLabels();
      }
    });
    
    // Quantity changes (for one-length template)
    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"]')) {
        console.log('Quantity changed, updating price');
        this.updatePrice();
      }
    });
    
    // Club selection changes (for default template)
    document.addEventListener('change', (e) => {
      if (e.target.matches('.club-checkbox')) {
        console.log('Club selection changed, updating price');
        this.updatePrice();
      }
    });
    
    // Initial price calculation
    setTimeout(() => {
      console.log('Performing initial price calculation...');
      this.updatePrice();
    }, 500); // Small delay to ensure all elements are initialized
    
    console.log('✅ Price update triggers setup complete');
  }

  handleColorSwatchClick(colorKey, colorName, productImage) {
    console.log('Color swatch clicked:', { colorKey, colorName, productImage });
    
    // Update the dropdown selection (for default template custom properties)
    if (this.elements.color) {
      const oldValue = this.elements.color.value;
      this.elements.color.value = colorKey;
      console.log('Updated color dropdown from:', oldValue, 'to:', colorKey);
      
      // Trigger change event to ensure any other listeners are notified
      this.elements.color.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      console.warn('Color dropdown element not found');
    }
    
    // Update hidden color input (for one-length template)
    const hiddenColorInput = document.querySelector('input[name="properties[Color]"]');
    if (hiddenColorInput) {
      hiddenColorInput.value = colorName;
      console.log('Updated hidden color input to:', colorName);
    }
    
    // Update product image based on color selection
    this.updateProductImage(productImage);
    
    // Update price calculation when color changes
    this.updatePrice();
    
    // Update color selection display
    const selectedColorDisplay = document.getElementById('selected-color-name');
    if (selectedColorDisplay) {
      selectedColorDisplay.textContent = colorName;
    }
    
    // Update visual selection state
    this.updateSwatchSelection(colorKey);
    
    // Change the product image
    this.changeProductImage(productImage);
    
    // Validate form after selection
    this.validateForm();
  }

  updateSwatchSelection(selectedColorKey) {
    console.log('🎨 Updating swatch selection for:', selectedColorKey);
    
    // Remove active class from all swatches
    const allSwatches = this.elements.colorSwatchContainer?.querySelectorAll('.color-swatch-wrapper');
    console.log('🎨 Found swatches:', allSwatches?.length);
    
    allSwatches?.forEach(swatch => {
      swatch.classList.remove('selected');
      console.log('🎨 Removed selected from:', swatch.getAttribute('data-color-key'));
    });
    
    // Add active class to selected swatch
    const selectedSwatch = this.elements.colorSwatchContainer?.querySelector(`[data-color-key="${selectedColorKey}"]`);
    console.log('🎨 Looking for swatch with data-color-key:', selectedColorKey);
    console.log('🎨 Found selected swatch:', selectedSwatch);
    
    if (selectedSwatch) {
      selectedSwatch.classList.add('selected');
      console.log('🎨 Added selected class to:', selectedSwatch.getAttribute('data-color-key'));
    } else {
      console.warn('🎨 Could not find swatch with data-color-key:', selectedColorKey);
      // Let's try to find what swatches actually exist
      const allSwatchesDebug = this.elements.colorSwatchContainer?.querySelectorAll('[data-color-key]');
      console.log('🎨 Available color keys:', Array.from(allSwatchesDebug || []).map(s => s.getAttribute('data-color-key')));
    }
  }

  changeProductImage(productImageName) {
    console.log('Attempting to change product image to:', productImageName);
    
    // Try multiple selectors to find the main product image
    const selectors = [
      '.product__media img',
      '.product-media img',
      '.featured-product__photo img',
      '.product-single__photo img',
      '.product__photo img',
      'img[data-product-image]',
      '.media img'
    ];
    
    let productImage = null;
    for (const selector of selectors) {
      productImage = document.querySelector(selector);
      if (productImage) {
        console.log('Found product image with selector:', selector);
        break;
      }
    }
    
    if (productImage && productImageName) {
      // Use proper Shopify asset URL for product images
      const newImageSrc = window.getAssetUrl ? window.getAssetUrl(productImageName) : `/assets/${productImageName}`;
      console.log('Changing image src to:', newImageSrc);
      productImage.src = newImageSrc;
      productImage.alt = `Product in selected color`;
    } else {
      console.warn('Could not find product image element or missing image name');
    }
  }

  handleColorDropdownChange(event) {
    const selectedColorKey = event.target.value;
    console.log('Color dropdown changed to:', selectedColorKey);
    
    if (selectedColorKey && this.colorData && this.colorData.colors[selectedColorKey]) {
      // Update visual selection state
      this.updateSwatchSelection(selectedColorKey);
      
      // Update product image based on color selection
      const colorInfo = this.colorData.colors[selectedColorKey];
      this.updateProductImage(colorInfo.productImage);
      
      // Update price calculation when color changes
      this.updatePrice();
    }
    
    // Validate form after selection
    this.validateForm();
  }

  updateProductImage(productImageFilename) {
    if (!productImageFilename) {
      console.log('No product image filename provided - keeping default image');
      return;
    }
    
    console.log('🖼️ Updating product image to:', productImageFilename);
    
    // Build the full URL for the product image from assets using proper Shopify asset URL
    const imageUrl = window.getAssetUrl ? window.getAssetUrl(productImageFilename) : `/assets/${productImageFilename}`;
    console.log('Image URL:', imageUrl);
    
    // Find all product media images in the gallery - try multiple selectors
    const selectors = [
      '.product__media img',
      '.product-media-container img',
      '.media img',
      '.product-single__media img',
      '[data-media-id] img',
      '.slider__slide img',
      // Lightbox/zoom modal images (product-media-modal) render the full-size
      // view from Shopify product media, so they must be swapped too or the
      // zoom shows the default image instead of the selected color.
      '.product-media-modal__content img',
      'img[data-media-id]'
    ];
    
    let foundImages = [];
    selectors.forEach(selector => {
      const images = document.querySelectorAll(selector);
      if (images.length > 0) {
        console.log(`Found ${images.length} images with selector: ${selector}`);
        foundImages = [...foundImages, ...Array.from(images)];
      }
    });
    
    // Remove duplicates
    foundImages = [...new Set(foundImages)];
    
    if (foundImages.length > 0) {
      console.log(`Found ${foundImages.length} total product image elements to update`);
      
      // Update all found images
      foundImages.forEach((img, index) => {
        console.log(`Updating product image ${index + 1}:`, img);
        
        // Store original src as fallback if needed
        if (!img.dataset.originalSrc) {
          img.dataset.originalSrc = img.src;
        }
        
        // Update the src attribute
        img.src = imageUrl;
        
        // Also update srcset if it exists (for responsive images)
        if (img.hasAttribute('srcset')) {
          img.srcset = imageUrl;
        }
        
        // Update data-src if lazy loading is used
        if (img.hasAttribute('data-src')) {
          img.setAttribute('data-src', imageUrl);
        }
        
        // Handle image load error - fallback to original
        img.onerror = () => {
          console.warn(`Failed to load color image: ${imageUrl}, falling back to original`);
          if (img.dataset.originalSrc) {
            img.src = img.dataset.originalSrc;
          }
        };
      });
      
      console.log('✅ Successfully updated product images');
    } else {
      console.warn('❌ No product image elements found to update');
      console.log('Available image elements on page:');
      document.querySelectorAll('img').forEach((img, i) => {
        console.log(`  ${i + 1}. ${img.src} (classes: ${img.className})`);
      });
    }
  }

  updateSelectedClubsValue() {
    const checkedClubs = document.querySelectorAll('.club-checkbox:checked');
    const selectedClubValues = Array.from(checkedClubs).map(checkbox => checkbox.getAttribute('data-club-value'));
    
    if (this.elements.selectedClubsCombined) {
      this.elements.selectedClubsCombined.value = selectedClubValues.join(', ');
      console.log('Updated selected clubs value:', this.elements.selectedClubsCombined.value);
    }
  }

  handleSelectAllChange(isChecked) {
    console.log('Select All changed:', isChecked);
    const clubCheckboxes = document.querySelectorAll('.club-checkbox');
    
    clubCheckboxes.forEach(checkbox => {
      checkbox.checked = isChecked;
    });
    
    // Update the combined value, price, and validate form
    this.updateSelectedClubsValue();
    this.updatePrice();
    this.validateForm();
  }

  addBasePriceLabel() {
    console.log('🏷️ Adding base price label for one-length-new template');
    
    // Add a simple test element first to verify this is working
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      background: red;
      color: white;
      padding: 10px;
      margin: 10px 0;
      font-weight: bold;
    `;
    testElement.textContent = 'TEST: Base price label function is running!';
    
    // Try to add it to multiple possible locations
    const possibleTargets = [
      document.querySelector('.price-block'),
      document.querySelector('[id*="price-"]'),
      document.querySelector('.price'),
      document.querySelector('.product-form'),
      document.querySelector('main')
    ];
    
    for (const target of possibleTargets) {
      if (target) {
        target.appendChild(testElement);
        console.log('✅ Test element added to:', target.className || target.tagName);
        break;
      }
    }
    
    // Find the price block
    const priceBlock = document.querySelector('.price-block') || 
                      document.querySelector('[id*="price-"]') ||
                      document.querySelector('.price');
    
    if (!priceBlock) {
      console.log('❌ Price block not found for base price label');
      return;
    }
    
    // Check if label already exists
    if (document.querySelector('.base-price-label')) {
      console.log('Base price label already exists');
      return;
    }
    
    // Create the base price label
    const basePriceLabel = document.createElement('div');
    basePriceLabel.className = 'base-price-label';
    basePriceLabel.style.cssText = `
      font-size: 0.9em;
      color: #666;
      margin-bottom: 0.5rem;
      font-weight: normal;
    `;
    basePriceLabel.textContent = '$12.99 base price + club price';
    
    // Insert the label before the price block
    priceBlock.parentNode.insertBefore(basePriceLabel, priceBlock);
    
    console.log('✅ Base price label added successfully');
  }

  updateSelectAllState() {
    if (!this.elements.selectAllClubs) return;
    
    const clubCheckboxes = document.querySelectorAll('.club-checkbox');
    const checkedClubs = document.querySelectorAll('.club-checkbox:checked');
    
    // Update Select All state based on individual checkboxes
    if (checkedClubs.length === 0) {
      // No clubs selected
      this.elements.selectAllClubs.checked = false;
      this.elements.selectAllClubs.indeterminate = false;
    } else if (checkedClubs.length === clubCheckboxes.length) {
      // All clubs selected
      this.elements.selectAllClubs.checked = true;
      this.elements.selectAllClubs.indeterminate = false;
    } else {
      // Some clubs selected (indeterminate state)
      this.elements.selectAllClubs.checked = false;
      this.elements.selectAllClubs.indeterminate = true;
    }
  }

  updatePrice() {
    console.log('🧮 Starting price calculation...');
    
    // Find price elements with multiple selectors for both templates
    const priceSelectors = [
      '.price .price-item--regular',
      '.price__regular .price-item--regular', 
      '.price-item--regular',
      '[id^="price-"] .price-item--regular',
      '.price--large .price-item--regular'
    ];
    
    let priceElement = null;
    for (const selector of priceSelectors) {
      priceElement = document.querySelector(selector);
      if (priceElement) {
        console.log(`Found price element with selector: ${selector}`);
        break;
      }
    }
    
    if (!priceElement) {
      console.log('❌ No price element found, skipping price update');
      console.log('Available elements on page:');
      document.querySelectorAll('[class*="price"]').forEach((el, i) => {
        console.log(`  ${i + 1}. ${el.className} - ${el.tagName}`);
      });
      return;
    }
    
    // Get current selections
    const selections = this.getCurrentSelections();
    console.log('Current selections:', selections);
    
    // Calculate price based on template type
    const pricing = this.calculatePrice(selections);
    console.log('Calculated pricing:', pricing);
    
    // Update price displays
    this.updatePriceDisplays(priceElement, pricing, selections);
    
    // Update hidden form fields
    this.updateHiddenFields(pricing, selections);
  }
  
  getCurrentSize() {
    const selectedSizeInput = document.querySelector('input[name="properties[Size]"]:checked');
    return selectedSizeInput ? selectedSizeInput.value : 'Adult'; // Default to Adult
  }

  updateTierPricingLabels() {
    console.log('🏷️ Updating tier pricing labels...');
    
    const tierContainers = document.querySelectorAll('.color-tier');
    
    tierContainers.forEach((tierContainer, index) => {
      // Use the multiplier the group was tagged with (set in initializeColorSwatches)
      // so Sale (0.8) maps to tier 5 rather than its position in the list.
      const containerMultiplier = parseFloat(tierContainer.getAttribute('data-multiplier'));
      const tierNumber = this.getTierNumberForMultiplier(containerMultiplier) || (index + 1);
      const tierTitle = tierContainer.querySelector('.color-tier-title');

      if (!tierTitle) return;
      
      // Get the correct tier price based on template and age group
      let tierPrice = '7.00'; // fallback
      
      try {
        // Determine template type by checking for size selector elements (only exists in one-length template)
        const isOneLengthTemplate = document.querySelector('input[name="properties[Size]"]') ||
                                   document.querySelector('.size-selector') ||
                                   window.location.pathname.includes('one-length');
        
        // Get current size selection
        const selectedSize = this.getCurrentSize();
        
        if (isOneLengthTemplate) {
          // One-length template
          const ageGroup = selectedSize === 'Child' ? 'Child' : 'Adult';
          const tierKey = `oneLength${ageGroup}Tier${tierNumber}`;
          
          if (this.tierProducts?.tierProducts?.[tierKey]?.tierPricePerClub) {
            tierPrice = this.tierProducts.tierProducts[tierKey].tierPricePerClub.toFixed(2);
          }
        } else {
          // Default template
          const tierKey = `tier${tierNumber}`;
          
          if (this.tierProducts?.tierProducts?.[tierKey]?.tierPricePerClub) {
            tierPrice = this.tierProducts.tierProducts[tierKey].tierPricePerClub.toFixed(2);
          }
        }
        
        console.log(`Updated ${this.getTierLabel(tierNumber)} price: $${tierPrice}/club (template: ${isOneLengthTemplate ? 'one-length' : 'default'}, size: ${selectedSize})`);
        tierTitle.textContent = `${this.getTierLabel(tierNumber)} - $${tierPrice}/club`;
      } catch (error) {
        console.error('Error updating tier price label:', error);
      }
    });
  }

  getCurrentSelections() {
    // Get selected size
    const selectedSizeInput = document.querySelector('input[name="properties[Size]"]:checked');
    const selectedSize = selectedSizeInput ? selectedSizeInput.value : 'Adult'; // Default to Adult
    
    // Get selected color and its multiplier
    let selectedColor = null;
    let colorMultiplier = 1;
    
    // Try color dropdown first (for default template)
    if (this.elements.color && this.elements.color.value) {
      selectedColor = this.elements.color.value;
    } else {
      // Try hidden color input (for one-length template)
      const hiddenColorInput = document.querySelector('input[name="properties[Color]"]');
      if (hiddenColorInput && hiddenColorInput.value) {
        // Find the color key by name
        if (this.colorData && this.colorData.colors) {
          for (const [key, colorData] of Object.entries(this.colorData.colors)) {
            if (colorData.name === hiddenColorInput.value) {
              selectedColor = key;
              break;
            }
          }
        }
      }
    }
    
    // Get color multiplier
    if (selectedColor && this.colorData?.colors[selectedColor]) {
      colorMultiplier = this.colorData.colors[selectedColor].multiplier || 1;
    }
    
    // Get selected clubs count (for default template with custom properties)
    const selectedClubs = document.querySelectorAll('.club-checkbox:checked');
    const clubCount = selectedClubs.length;
    
    // Get quantity (for one-length template)
    const quantityInput = document.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    
    return {
      size: selectedSize,
      color: selectedColor,
      colorMultiplier: colorMultiplier,
      clubCount: clubCount,
      quantity: quantity,
      hasCustomProperties: !!this.elements.brand // Check if this is default template with custom properties
    };
  }
  
  calculatePrice(selections) {
    console.log('💰 Calculating price for selections:', selections);
    
    if (selections.hasCustomProperties) {
      // Default template with custom club selection
      return this.calculateCustomPrice(selections);
    } else {
      // One-length template with quantity-based pricing
      return this.calculateQuantityPrice(selections);
    }
  }
  
  calculateCustomPrice(selections) {
    // Custom template pricing: base price + (number of clubs × per-club price × color multiplier)
    // For custom template, we use the default tier 1 pricing as base
    const basePrice = 1299; // $12.99 in cents
    const perClubBasePrice = selections.size === 'Adult' ? 700 : 600; // Adult: $7.00, Child: $6.00 per club base
    
    // Apply color multiplier to the per-club price
    const perClubPrice = Math.round(perClubBasePrice * selections.colorMultiplier);
    
    const clubsTotal = selections.clubCount * perClubPrice;
    const totalPrice = basePrice + clubsTotal;
    
    return {
      basePrice: basePrice,
      perClubBasePrice: perClubBasePrice,
      perClubPrice: perClubPrice,
      clubsTotal: clubsTotal,
      totalPrice: totalPrice,
      formattedPrice: this.formatPrice(totalPrice),
      priceBreakdown: {
        base: this.formatPrice(basePrice),
        perClubBase: this.formatPrice(perClubBasePrice),
        perClubFinal: this.formatPrice(perClubPrice),
        clubs: `${selections.clubCount} × ${this.formatPrice(perClubPrice)}`,
        clubsTotal: this.formatPrice(clubsTotal),
        colorMultiplier: selections.colorMultiplier
      }
    };
  }
  
  calculateQuantityPrice(selections) {
    // One-length template pricing: find appropriate tier product and use predefined variant prices
    const tierProduct = this.findTierProduct(selections);
    
    if (!tierProduct) {
      // Fallback pricing using base calculation
      const fallbackBasePrice = 1299; // $12.99 in cents
      const fallbackPerClubPrice = selections.size === 'Adult' ? 800 : 600; // $8.00 or $6.00 per club
      const fallbackPrice = fallbackBasePrice + (fallbackPerClubPrice * selections.quantity);
      return {
        totalPrice: fallbackPrice,
        formattedPrice: this.formatPrice(fallbackPrice),
        tierProduct: null,
        error: 'No tier product found'
      };
    }

    // For one-length templates, use the predefined variant prices
    // These already include the tier multiplier, so no need to apply color multiplier again
    const variantKey = selections.quantity.toString();
    if (tierProduct.variants && tierProduct.variants[variantKey]) {
      const variantPrice = Math.round(tierProduct.variants[variantKey].price * 100); // Convert to cents
      
      return {
        basePrice: Math.round(tierProduct.basePrice * 100),
        perClubBasePrice: Math.round(tierProduct.perClubBasePrice * 100),
        perClubPrice: Math.round(tierProduct.perClubPrice * 100),
        tierMultiplier: tierProduct.multiplier || 1,
        totalPrice: variantPrice,
        formattedPrice: this.formatPrice(variantPrice),
        tierProduct: tierProduct,
        priceBreakdown: {
          base: this.formatPrice(Math.round(tierProduct.basePrice * 100)),
          perClubBase: this.formatPrice(Math.round(tierProduct.perClubBasePrice * 100)),
          perClubFinal: this.formatPrice(Math.round(tierProduct.perClubPrice * 100)),
          tierMultiplier: tierProduct.multiplier,
          variant: this.formatPrice(variantPrice),
          quantity: selections.quantity
        }
      };
    }

    // Fallback to calculation if variant not found
    const basePrice = Math.round(tierProduct.basePrice * 100); // Convert to cents
    const perClubPrice = Math.round(tierProduct.perClubPrice * 100); // Use the calculated per-club price
    
    // Price = Base Price + (Per Club Price × Quantity) 
    // Note: tierProduct.perClubPrice already includes the tier multiplier
    const totalPrice = basePrice + (perClubPrice * selections.quantity);
    
    return {
      basePrice: basePrice,
      perClubBasePrice: Math.round(tierProduct.perClubBasePrice * 100), // Original base price per club
      perClubPrice: perClubPrice, // Final per-club price with tier multiplier
      tierMultiplier: tierProduct.multiplier || 1,
      totalPrice: totalPrice,
      formattedPrice: this.formatPrice(totalPrice),
      tierProduct: tierProduct,
      priceBreakdown: {
        base: this.formatPrice(basePrice),
        perClubBase: this.formatPrice(Math.round(tierProduct.perClubBasePrice * 100)),
        perClubFinal: this.formatPrice(perClubPrice),
        tierMultiplier: tierProduct.multiplier,
        quantity: selections.quantity
      }
    };
  }
  
  findTierProduct(selections) {
    if (!this.tierProducts || !this.tierProducts.tierProducts) {
      console.warn('No tier products data available');
      return null;
    }
    
    // Determine template type and age group
    const ageGroup = selections.size.toLowerCase(); // 'adult' or 'child'
    
    // Determine tier based on color multiplier (0.8 = Sale = tier 5)
    const tierNumber = this.getTierNumberForMultiplier(selections.colorMultiplier) || 1;

    // Build the tier key based on age group and tier number
    const tierKey = `oneLength${ageGroup.charAt(0).toUpperCase() + ageGroup.slice(1)}Tier${tierNumber}`;
    
    const tierProduct = this.tierProducts.tierProducts[tierKey];
    console.log(`Looking for tier product: ${tierKey} (multiplier: ${selections.colorMultiplier})`, tierProduct);
    
    return tierProduct;
  }
  
  updatePriceDisplays(priceElement, pricing, selections) {
    console.log('🏷️ Updating price displays...');
    
    // Check if this is the one-length-new template
    const isOneLengthNewTemplate = window.location.pathname.includes('one-length-new');
    
    // Update main price element
    let priceText = pricing.formattedPrice;
    
    // For one-length-new template, show base price only
    if (isOneLengthNewTemplate && pricing.basePrice) {
      priceText = this.formatPrice(pricing.basePrice);
      console.log('One-length-new template: showing base price:', priceText);
    } else if (selections.hasCustomProperties) {
      // Add context for default template
      if (selections.clubCount === 0) {
        priceText += ' (Base Price)';
      } else {
        priceText += ` (${selections.clubCount} club${selections.clubCount > 1 ? 's' : ''})`;
      }
    }
    
    priceElement.textContent = priceText;
    console.log('Updated main price to:', priceText);
    
    // Update any secondary price elements
    const allPriceElements = document.querySelectorAll('.price-item--regular');
    allPriceElements.forEach((el, index) => {
      if (el !== priceElement) {
        el.textContent = pricing.formattedPrice;
        console.log(`Updated secondary price element ${index + 1} to:`, pricing.formattedPrice);
      }
    });
  }

  updateHiddenFields(pricing, selections) {
    // Update hidden tracking fields for form submission
    const fields = [
      { selector: '#properties_calculated_total', value: pricing.formattedPrice },
      { selector: '#properties_club_count', value: selections.clubCount.toString() },
      { selector: '#properties_color_multiplier', value: selections.colorMultiplier.toString() },
      { selector: '#properties_additional_cost', value: pricing.clubsTotal ? this.formatPrice(pricing.clubsTotal) : '0' }
    ];
    
    fields.forEach(field => {
      const element = document.querySelector(field.selector);
      if (element) {
        element.value = field.value;
        console.log(`Updated ${field.selector} to:`, field.value);
      }
    });
  }

  // Legacy method for compatibility
  updatePriceLegacy() {
    if (!this.elements.priceElement) {
      console.log('Price element not found, skipping price update');
      return;
    }

    // Get selected clubs count
    const selectedClubs = document.querySelectorAll('.club-checkbox:checked');
    const clubCount = selectedClubs.length;

    // Get selected color multiplier
    let multiplier = 1;
    if (this.elements.color && this.elements.color.value && this.colorData?.colors) {
      const selectedColor = this.colorData.colors[this.elements.color.value];
      if (selectedColor && selectedColor.multiplier) {
        multiplier = selectedColor.multiplier;
      }
    }

    // Calculate total price: base price + (club count × club price × multiplier)
    const clubsTotal = clubCount * this.clubPrice * multiplier;
    const totalPrice = this.basePrice + clubsTotal;

    // Format price for display
    const formattedPrice = this.formatPrice(totalPrice);
    
    // Update the price display
    console.log('Updating price:', {
      clubCount,
      multiplier,
      clubsTotal: clubsTotal / 100,
      totalPrice: totalPrice / 100,
      formattedPrice
    });

    // Update main price element - show "(Base Price)" only when no clubs are selected
    let priceText = formattedPrice;
    if (clubCount === 0) {
      priceText += ' (Base Price)';
    }
    this.elements.priceElement.textContent = priceText;

    // Update checkout price element (without the base price label)
    if (this.elements.checkoutPriceElement) {
      this.elements.checkoutPriceElement.textContent = formattedPrice;
    }

    // Update hidden price tracking fields for cart submission
    if (this.elements.calculatedTotal) {
      this.elements.calculatedTotal.value = formattedPrice;
    }
    if (this.elements.clubCount) {
      this.elements.clubCount.value = clubCount.toString();
    }
    if (this.elements.colorMultiplier) {
      this.elements.colorMultiplier.value = multiplier.toString();
    }
    if (this.elements.additionalCost) {
      // Calculate just the additional cost (clubs × price × multiplier)
      const additionalCostFormatted = this.formatPrice(clubsTotal);
      this.elements.additionalCost.value = additionalCostFormatted;
    }

    // Add pricing information to cart note if clubs are selected
    this.updateCartNote(clubCount, clubsTotal, totalPrice, formattedPrice);
  }

  updateCartNote(clubCount, clubsTotal, totalPrice, formattedPrice) {
    // Find or create cart note input
    let cartNote = document.querySelector('input[name="note"], textarea[name="note"]');
    if (!cartNote) {
      // Create hidden cart note input if it doesn't exist
      cartNote = document.createElement('input');
      cartNote.type = 'hidden';
      cartNote.name = 'note';
      if (this.elements.form) {
        this.elements.form.appendChild(cartNote);
      }
    }

    if (clubCount > 0) {
      const basePrice = this.formatPrice(this.basePrice);
      const additionalCost = this.formatPrice(clubsTotal);
      const noteText = `Custom Club Wrapping Order\nBase Price: ${basePrice}\nClubs Selected: ${clubCount}\nAdditional Cost: ${additionalCost}\nTotal Expected: ${formattedPrice}`;
      cartNote.value = noteText;
    } else {
      cartNote.value = '';
    }
  }

  formatPrice(priceInCents) {
    // Convert cents to dollars and format
    const dollars = priceInCents / 100;
    
    // Check if currency code is enabled (from Shopify settings)
    const currencyCodeEnabled = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    
    if (currencyCodeEnabled) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(dollars) + ' USD';
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(dollars);
    }
  }
}

// CSP-compliant initialization without global function assignment
(function() {
  'use strict';
  
  // Initialize when DOM is loaded
  document.addEventListener('DOMContentLoaded', () => {
    console.log('🚀 DOM loaded, checking for iron-wrap-one-length product...');
    
    const productInfo = document.querySelector('product-info[data-url]');
    const productUrl = productInfo?.getAttribute('data-url') || '';
    console.log('🚀 Product URL:', productUrl);
    const colorSwatchContainer = document.getElementById('color-swatches-container');
    console.log('🚀 Color swatch container found:', !!colorSwatchContainer);
    
    console.log('🚀 Initializing custom line item properties...');
    new CustomLineItemProperties();
  });

  // Also try initializing after a delay to ensure all elements are loaded
  setTimeout(() => {
    console.log('Delayed initialization attempt...');
    if (!window.customLineItemPropertiesInitialized) {
      new CustomLineItemProperties();
    }
  }, 1000);

  // Reinitialize when product form updates (for variant changes)
  document.addEventListener('variant:change', () => {
    console.log('Variant change detected, reinitializing...');
    new CustomLineItemProperties();
  });
})();
